import React, { useState, useRef, useEffect } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import styles from './MultiSearchableSelect.module.scss'

export interface MultiSearchableSelectOption {
  value: number
  label: string
}

interface MultiSearchableSelectProps {
  selectedValues: number[]
  onChange: (values: number[]) => void
  options: MultiSearchableSelectOption[]
  placeholder?: string
  disabled?: boolean
  className?: never
}

export const MultiSearchableSelect: React.FC<MultiSearchableSelectProps> = ({
  selectedValues, onChange, options, placeholder = 'Select…', disabled = false,
}) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const filtered = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  )

  const selectedOptions = options.filter((opt) => selectedValues.includes(opt.value))

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggle = (optValue: number) => {
    if (selectedValues.includes(optValue)) {
      onChange(selectedValues.filter((v) => v !== optValue))
    } else {
      onChange([...selectedValues, optValue])
    }
  }

  const handleRemove = (e: React.MouseEvent, optValue: number) => {
    e.stopPropagation()
    onChange(selectedValues.filter((v) => v !== optValue))
  }

  const handleOpen = () => {
    if (disabled) return
    setOpen(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  return (
    <div ref={containerRef} className={styles.container}>
      <button type="button" onClick={handleOpen} disabled={disabled} className={styles.trigger}>
        {selectedOptions.length > 0 ? (
          <>
            {selectedOptions.map((opt) => (
              <span key={opt.value} className={styles.selectedBadge}>
                {opt.label}
                <span role="button" tabIndex={-1} onClick={(e) => handleRemove(e, opt.value)} onKeyDown={() => {}} className={styles.removeBadge}>
                  <X style={{ width: 12, height: 12 }} />
                </span>
              </span>
            ))}
          </>
        ) : (
          <span className={styles.placeholder}>{placeholder}</span>
        )}
        <span className={styles.chevronWrap}>
          <ChevronDown className={[styles.chevron, open ? styles.chevronOpen : ''].filter(Boolean).join(' ')} />
        </span>
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrap}>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tags…"
              className={styles.searchInput}
            />
          </div>
          <ul className={styles.optionsList}>
            {filtered.length === 0 ? (
              <li className={styles.noResults}>No results found</li>
            ) : (
              filtered.map((opt) => {
                const isSelected = selectedValues.includes(opt.value)
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      onClick={() => handleToggle(opt.value)}
                      className={[styles.option, isSelected ? styles.optionSelected : ''].filter(Boolean).join(' ')}
                    >
                      <span className={[styles.checkbox, isSelected ? styles.checkboxChecked : ''].filter(Boolean).join(' ')}>
                        {isSelected && <Check style={{ width: 12, height: 12, color: '#fff' }} strokeWidth={3} />}
                      </span>
                      <span className={isSelected ? styles.optionLabelActive : styles.optionLabel}>{opt.label}</span>
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
